import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { grey, green, blue } from '@mui/material/colors';

// Per workspace, which parser takes its ocean uploads: CargoFive (the default) or Monada's own
// agent. Switching a shipment type to the agent is the moment the agent takes over those files,
// so it is decided here, next to the agent's rules and training data, not in workspace settings.

const PARSER_LABEL = { cargofive: 'CargoFive', monada: 'Monada agent' };

export default function OceanRouting({ accesstoken }) {
    const [workspaces, setWorkspaces] = useState(null);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [onlyAgent, setOnlyAgent] = useState(false);
    const [pending, setPending] = useState(null); // { workspace, shipmentType, parser }
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        axios.get(`/api/admin/rate-agent/ocean-routing?accesstoken=${accesstoken}`)
            .then(({ data }) => setWorkspaces(data.workspaces))
            .catch(e => setError(e.response?.data?.detail || e.message));
    }, [accesstoken]);

    const shown = useMemo(() => (workspaces || []).filter(w => {
        if (onlyAgent && w.fcl === 'cargofive' && w.lcl === 'cargofive') return false;
        const q = search.trim().toLowerCase();
        return !q || `${w.title} ${w.alias}`.toLowerCase().includes(q);
    }), [workspaces, search, onlyAgent]);

    const onAgent = (workspaces || []).filter(w => w.fcl === 'monada' || w.lcl === 'monada').length;

    async function confirm() {
        setSaving(true);
        setSaveError(null);
        try {
            const { data } = await axios.put(`/api/admin/rate-agent/ocean-routing/${pending.workspace.id}?accesstoken=${accesstoken}`,
                { shipmentType: pending.shipmentType, parser: pending.parser });
            setWorkspaces(ws => ws.map(w => (w.id === data.id ? data : w)));
            setPending(null);
        } catch (e) {
            setSaveError(e.response?.data?.detail || e.message);
        }
        setSaving(false);
    }

    if (error) return <Alert severity='error'>{error}</Alert>;
    if (!workspaces) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    const choice = (w, shipmentType) => {
        const value = shipmentType === 'FCL' ? w.fcl : w.lcl;
        return (
            <ToggleButtonGroup exclusive size='small' value={value}
                onChange={(e, v) => v && v !== value && setPending({ workspace: w, shipmentType, parser: v })}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: '2px', px: 1.5, fontSize: '12px' },
                    '& .Mui-selected[value="monada"]': { background: `${green[50]} !important`, color: `${green[800]} !important` },
                    '& .Mui-selected[value="cargofive"]': { background: `${blue[50]} !important`, color: `${blue[800]} !important` } }}>
                <ToggleButton value='cargofive'>CargoFive</ToggleButton>
                <ToggleButton value='monada'>Monada agent</ToggleButton>
            </ToggleButtonGroup>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant='body2' sx={{ color: grey[700], maxWidth: '90ch' }}>
                Choose who parses each workspace's ocean uploads. Switching FCL or LCL to the Monada agent means those files stop going to CargoFive from the next upload on. When a workspace has either one on the agent, its upload dialog asks whether a sea file is FCL or LCL.
            </Typography>
            <Alert severity='info' sx={{ maxWidth: '90ch' }}>
                The agent's parser isn't built yet. Until it is, files sent to the Monada agent land in Imports for you to convert by hand, the way air files do today.
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size='small' label='Search workspace' value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
                <ToggleButtonGroup exclusive size='small' value={onlyAgent ? 'agent' : 'all'} onChange={(e, v) => v && setOnlyAgent(v === 'agent')}
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}>
                    <ToggleButton value='all'>All workspaces ({workspaces.length})</ToggleButton>
                    <ToggleButton value='agent'>On the Monada agent ({onAgent})</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Workspace</TableCell>
                            <TableCell>CargoFive connected</TableCell>
                            <TableCell>FCL uploads parsed by</TableCell>
                            <TableCell>LCL uploads parsed by</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shown.map(w => (
                            <TableRow key={w.id}>
                                <TableCell>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{w.title}</Typography>
                                    <Typography sx={{ fontSize: '12px', color: grey[600] }}>{w.alias}</Typography>
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: w.cargofiveEnabled ? green[800] : grey[500] }}>{w.cargofiveEnabled ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{choice(w, 'FCL')}</TableCell>
                                <TableCell>{choice(w, 'LCL')}</TableCell>
                            </TableRow>
                        ))}
                        {shown.length === 0 && (
                            <TableRow><TableCell colSpan={4} sx={{ color: grey[600], fontSize: '13px' }}>No workspaces match.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {pending && (
                <Dialog open={true} onClose={saving ? undefined : () => setPending(null)} maxWidth='sm' fullWidth>
                    <DialogTitle>Send {pending.shipmentType} uploads from {pending.workspace.title} to {PARSER_LABEL[pending.parser]}?</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography variant='body2'>
                            {pending.parser === 'monada'
                                ? `From the next upload on, ${pending.shipmentType} files from this workspace go to the Monada agent instead of CargoFive. The upload dialog will ask whether each sea file is FCL or LCL.`
                                : `From the next upload on, ${pending.shipmentType} files from this workspace go back to CargoFive.`}
                        </Typography>
                        <Typography variant='body2' sx={{ color: grey[700] }}>Files already uploaded stay where they are.</Typography>
                        {saveError && <Alert severity='error'>{saveError}</Alert>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPending(null)} disabled={saving}>Cancel</Button>
                        <Button variant='contained' onClick={confirm} disabled={saving}>{saving ? 'Saving…' : `Send to ${PARSER_LABEL[pending.parser]}`}</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
}
